// src/pages/master/AgentsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Edit2,
  Filter,
  Plus,
  Search,
  Trash2,
  X,
} from "react-feather";
import { toast } from "react-toastify";

import ConfirmModal from "../../common_old/ConfirmModal";
import "../../../css/master-common.css";

import {
  clearAgentsError,
  createAgentThunk,
  deleteAgentThunk,
  fetchAgentsThunk,
  updateAgentThunk,
} from "../../../store/slices/agentsSlice";

import { getPerm } from "../../../utils/perm";

const ITEMS_PER_PAGE = 5;

const SORT_OPTIONS = [
  { value: "agent_id_asc", label: "Agent ID (Ascending)" },
  { value: "agent_id_desc", label: "Agent ID (Descending)" },
  { value: "ag_name_asc", label: "Agent Name (A-Z)" },
  { value: "ag_name_desc", label: "Agent Name (Z-A)" },
  { value: "mobile_asc", label: "Mobile (Ascending)" },
  { value: "mobile_desc", label: "Mobile (Descending)" },
];

const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const defaultForm = {
  agent_id: null,
  mobile: "",
  ag_name: "",
  password: "",
  status: "Active",
  water_access: 0,
  slrm_access: 0,
  profile_access: 0,
  setting_access: 0,
  balance_access: 0,
  waste_collection_access: 0,
  sale_entry_access: 0,
  income_access: 0,
  expense_access: 0,
};

const Pill = ({ children }) => <Badge className="mm-pill">{children}</Badge>;

const YesNoPill = ({ v }) => {
  const isYes = Number(v) === 1;
  return <Pill>{isYes ? "Yes" : "No"}</Pill>;
};

const StatusPill = ({ status }) => {
  const isActive = String(status || "").toLowerCase() === "active";
  return <Pill>{isActive ? "Active" : "Inactive"}</Pill>;
};

export default function AgentsPage() {
  const dispatch = useDispatch();
  const { list, loading, saving, error } = useSelector((s) => s.agents);

  const permsMap = useSelector((s) => s.permissions?.map) || {};
  const agentPerms = getPerm(permsMap, "agents") || {};
  const canCreate = !!agentPerms.can_create;
  const canUpdate = !!agentPerms.can_update;
  const canDelete = !!agentPerms.can_delete;

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState(defaultForm);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("agent_id_asc");
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchAgentsThunk());
  }, [dispatch]);

  const agentNameColWidth = useMemo(() => {
    const names = (list || []).map((a) => String(a.ag_name || ""));
    const longest = names.reduce((max, s) => Math.max(max, s.length), 0);
    const estimated = Math.round(longest * 8.5 + 90);
    return Math.min(520, Math.max(240, estimated));
  }, [list]);

  const sortLabel = useMemo(
    () => SORT_OPTIONS.find((x) => x.value === sortBy)?.label || "Sort",
    [sortBy]
  );

  const filterLabel = useMemo(
    () => FILTER_OPTIONS.find((x) => x.value === activeFilter)?.label || "All",
    [activeFilter]
  );

  const filteredRows = useMemo(() => {
    let result = Array.isArray(list) ? [...list] : [];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((a) => {
        const idMatch = String(a.agent_id || "").includes(term);
        const mobileMatch = String(a.mobile || "").toLowerCase().includes(term);
        const nameMatch = String(a.ag_name || "").toLowerCase().includes(term);
        return idMatch || mobileMatch || nameMatch;
      });
    }

    if (activeFilter !== "all") {
      result = result.filter((a) => {
        const st = String(a.status || "").toLowerCase();
        if (activeFilter === "active") return st === "active";
        if (activeFilter === "inactive") return st === "inactive";
        return true;
      });
    }

    result.sort((a, b) => {
      const aName = String(a.ag_name || "");
      const bName = String(b.ag_name || "");
      const aMobile = String(a.mobile || "");
      const bMobile = String(b.mobile || "");
      const aId = Number(a.agent_id || 0);
      const bId = Number(b.agent_id || 0);

      switch (sortBy) {
        case "agent_id_asc":
          return aId - bId;
        case "agent_id_desc":
          return bId - aId;
        case "ag_name_asc":
          return aName.localeCompare(bName);
        case "ag_name_desc":
          return bName.localeCompare(aName);
        case "mobile_asc":
          return aMobile.localeCompare(bMobile);
        case "mobile_desc":
          return bMobile.localeCompare(aMobile);
        default:
          return aId - bId;
      }
    });

    return result;
  }, [list, searchTerm, activeFilter, sortBy]);

  const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, activeFilter]);

  const getPaginationItems = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const isMobile = typeof window !== "undefined" && window.innerWidth < 576;
    const maxVisiblePages = isMobile ? 3 : 5;
    const goTo = (p) => setCurrentPage(Math.min(Math.max(1, p), totalPages));

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => goTo(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft size={16} className="me-1" />
        Prev
      </Pagination.Prev>
    );

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => goTo(1)}>
          1
        </Pagination.Item>
      );
      if (!isMobile && startPage > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => goTo(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (!isMobile && endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => goTo(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => goTo(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
        <ChevronRight size={16} className="ms-1" />
      </Pagination.Next>
    );

    return items;
  };

  const getSortIcon = (col) => {
    if (sortBy === `${col}_asc`) return <ChevronUp size={16} />;
    if (sortBy === `${col}_desc`) return <ChevronDown size={16} />;
    return null;
  };

  const openCreate = () => {
    if (!canCreate) return;
    setMode("create");
    setForm({ ...defaultForm });
    setShowModal(true);
    dispatch(clearAgentsError());
  };

  const openEdit = (a) => {
    if (!canUpdate) return;
    setMode("edit");
    setForm({
      agent_id: a.agent_id,
      mobile: a.mobile || "",
      ag_name: a.ag_name || "",
      password: "",
      status: a.status || "Active",
      water_access: Number(a.water_access) ? 1 : 0,
      slrm_access: Number(a.slrm_access) ? 1 : 0,
      profile_access: Number(a.profile_access) ? 1 : 0,
      setting_access: Number(a.setting_access) ? 1 : 0,
      balance_access: Number(a.balance_access) ? 1 : 0,
      waste_collection_access: Number(a.waste_collection_access) ? 1 : 0,
      sale_entry_access: Number(a.sale_entry_access) ? 1 : 0,
      income_access: Number(a.income_access) ? 1 : 0,
      expense_access: Number(a.expense_access) ? 1 : 0,
    });
    setShowModal(true);
    dispatch(clearAgentsError());
  };

  const closeModal = () => setShowModal(false);

  const AccessSwitch = ({ k, label }) => (
    <Form.Check
      type="switch"
      id={`acc-${k}`}
      label={label}
      checked={Number(form[k]) === 1}
      onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.checked ? 1 : 0 }))}
    />
  );

  const onSubmit = async (e) => {
    e.preventDefault();

    const mobile = String(form.mobile || "").trim();
    const ag_name = String(form.ag_name || "").trim();
    const status = form.status === "Inactive" ? "Inactive" : "Active";
    const password = String(form.password || "").trim();

    if (!mobile || !ag_name) return;

    if (!/^\d{10}$/.test(mobile)) {
      toast.error("Mobile number must be exactly 10 digits");
      return;
    }

    if (mode === "create" && !canCreate) return;
    if (mode === "edit" && !canUpdate) return;

    const accessPayload = {
      water_access: Number(form.water_access) ? 1 : 0,
      slrm_access: Number(form.slrm_access) ? 1 : 0,
      profile_access: Number(form.profile_access) ? 1 : 0,
      setting_access: Number(form.setting_access) ? 1 : 0,
      balance_access: Number(form.balance_access) ? 1 : 0,
      waste_collection_access: Number(form.waste_collection_access) ? 1 : 0,
      sale_entry_access: Number(form.sale_entry_access) ? 1 : 0,
      income_access: Number(form.income_access) ? 1 : 0,
      expense_access: Number(form.expense_access) ? 1 : 0,
    };

    if (mode === "create") {
      if (!password) return;

      const r = await dispatch(
        createAgentThunk({ mobile, ag_name, password, status, ...accessPayload })
      );
      if (createAgentThunk.fulfilled.match(r)) {
        toast.success("Agent created successfully ✅");
        setShowModal(false);
        setSearchTerm("");
      } else {
        toast.error(r?.payload || r?.error?.message || "Create failed");
      }
    } else {
      const payload = { mobile, ag_name, status, ...accessPayload };
      if (password) payload.password = password;

      const r = await dispatch(updateAgentThunk({ agent_id: form.agent_id, ...payload }));
      if (updateAgentThunk.fulfilled.match(r)) {
        toast.success("Agent updated successfully ✅");
        setShowModal(false);
      } else {
        toast.error(r?.payload || r?.error?.message || "Update failed");
      }
    }
  };

  const onDelete = (a) => {
    if (!canDelete) return;
    setDeleteTarget(a);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const r = await dispatch(deleteAgentThunk({ agent_id: deleteTarget.agent_id }));

      if (deleteAgentThunk.fulfilled.match(r)) {
        toast.success("Agent deleted successfully 🗑️");
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } else {
        toast.error(r?.payload || r?.error?.message || "Delete failed");
      }
    } finally {
      setDeleting(false);
    }
  };

  const clearSearch = () => setSearchTerm("");

  return (
    <div className="container-fluid px-3 py-4 mm-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1 fw-bold mm-title">Agents</h4>
        </div>

        <Button
          onClick={openCreate}
          disabled={!canCreate}
          className="mm-primary-btn d-flex align-items-center gap-2"
          title={!canCreate ? "No create permission" : "Add new agent"}
        >
          <Plus size={18} />
          Add Agent
        </Button>
      </div>

      {error ? (
        <Alert
          variant="danger"
          className="mm-card-soft mb-3"
          dismissible
          onClose={() => dispatch(clearAgentsError())}
        >
          <div className="fw-bold mb-1">Error</div>
          <div>{String(error)}</div>
        </Alert>
      ) : null}

      <Card className="mm-card-soft mb-3">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-end">
            <Col md={6}>
              <Form.Label className="fw-bold mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="mm-inputgroup-text">
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <FormControl
                  className="mm-input mm-inputgroup-control"
                  placeholder="Search by ID / Mobile / Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm ? (
                  <Button variant="light" className="mm-clear-btn" onClick={clearSearch}>
                    <X size={16} />
                  </Button>
                ) : null}
              </InputGroup>
            </Col>

            <Col md={3}>
              <Form.Label className="fw-bold mb-1">Sort</Form.Label>
              <Dropdown align="end">
                <Dropdown.Toggle className="mm-dropdown-btn w-100 d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    <Filter size={16} />
                    {sortLabel}
                  </span>
                  <ChevronDown size={16} />
                </Dropdown.Toggle>

                <Dropdown.Menu className="mm-dd-menu w-100">
                  {SORT_OPTIONS.map((opt) => (
                    <Dropdown.Item
                      key={opt.value}
                      active={sortBy === opt.value}
                      onClick={() => setSortBy(opt.value)}
                    >
                      {opt.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col md={3}>
              <Form.Label className="fw-bold mb-1">Filter</Form.Label>
              <Dropdown align="end">
                <Dropdown.Toggle className="mm-dropdown-btn w-100 d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    <Filter size={16} />
                    {filterLabel}
                  </span>
                  <ChevronDown size={16} />
                </Dropdown.Toggle>

                <Dropdown.Menu className="mm-dd-menu w-100">
                  {FILTER_OPTIONS.map((opt) => (
                    <Dropdown.Item
                      key={opt.value}
                      active={activeFilter === opt.value}
                      onClick={() => setActiveFilter(opt.value)}
                    >
                      {opt.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col md={12} className="text-end">
              <div className="text-muted small">
                Showing{" "}
                <span className="fw-bold">
                  {filteredRows.length === 0 ? 0 : startIndex + 1}-
                  {Math.min(startIndex + ITEMS_PER_PAGE, filteredRows.length)}
                </span>{" "}
                of <span className="fw-bold">{filteredRows.length}</span> agents
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mm-card">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">Loading agents...</div>
            </div>
          ) : (
            <>
              <div className="mm-table-wrap">
                <Table hover className="mm-table mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{ width: 90, cursor: "pointer" }}
                        onClick={() =>
                          setSortBy((p) =>
                            p === "agent_id_asc" ? "agent_id_desc" : "agent_id_asc"
                          )
                        }
                      >
                        <div className="d-flex align-items-center gap-1">
                          ID {getSortIcon("agent_id")}
                        </div>
                      </th>

                      <th
                        style={{ width: 170, cursor: "pointer" }}
                        onClick={() =>
                          setSortBy((p) => (p === "mobile_asc" ? "mobile_desc" : "mobile_asc"))
                        }
                      >
                        <div className="d-flex align-items-center gap-1">
                          Mobile {getSortIcon("mobile")}
                        </div>
                      </th>

                      <th
                        style={{ width: agentNameColWidth, cursor: "pointer" }}
                        onClick={() =>
                          setSortBy((p) => (p === "ag_name_asc" ? "ag_name_desc" : "ag_name_asc"))
                        }
                      >
                        <div className="d-flex align-items-center gap-1">
                          Name {getSortIcon("ag_name")}
                        </div>
                      </th>

                      <th style={{ width: 140 }}>Status</th>
                      <th style={{ width: 150, textAlign: "center" }}>Water Access</th>
                      <th style={{ width: 150, textAlign: "center" }}>SLRM Access</th>
                      <th style={{ width: 160, textAlign: "center" }}>Profile Access</th>
                      <th style={{ width: 170, textAlign: "center" }}>Settings Access</th>
                      <th style={{ width: 170, textAlign: "center" }}>Balance Access</th>
                      <th style={{ width: 220, textAlign: "center" }}>Waste Collection Access</th>
                      <th style={{ width: 170, textAlign: "center" }}>Sale Entry Access</th>
                      <th style={{ width: 170, textAlign: "center" }}>Income Access</th>
                      <th style={{ width: 170, textAlign: "center" }}>Expense Access</th>
                      <th className="mm-actions-th" style={{ width: 180, textAlign: "right" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="p-5 text-center">
                          <div>
                            <div className="mm-empty-icon mx-auto mb-2">
                              <Search size={22} />
                            </div>
                            <div className="fw-bold mb-1">
                              {searchTerm || activeFilter !== "all"
                                ? "No results found"
                                : "No agents found"}
                            </div>
                            <div className="text-muted">
                              {searchTerm || activeFilter !== "all"
                                ? "No agents match your search/filter."
                                : "Get started by adding your first agent."}
                            </div>

                            {(searchTerm || activeFilter !== "all") && (
                              <Button
                                variant="outline-primary"
                                className="mt-3"
                                onClick={() => {
                                  setSearchTerm("");
                                  setActiveFilter("all");
                                }}
                              >
                                Clear Filters
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((a) => (
                        <tr key={a.agent_id}>
                          <td className="text-muted fw-semibold">#{a.agent_id}</td>
                          <td>{a.mobile || <span className="text-muted">—</span>}</td>

                          <td
                            className="fw-bold"
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={a.ag_name}
                          >
                            {a.ag_name}
                          </td>

                          <td>
                            <StatusPill status={a.status} />
                          </td>

                          <td className="text-center">
                            <YesNoPill v={a.water_access} />
                          </td>
                          <td className="text-center">
                            <YesNoPill v={a.slrm_access} />
                          </td>
                          <td className="text-center">
                            <YesNoPill v={a.profile_access} />
                          </td>
                          <td className="text-center">
                            <YesNoPill v={a.setting_access} />
                          </td>
                          <td className="text-center">
                            <YesNoPill v={a.balance_access} />
                          </td>
                          <td className="text-center">
                            <YesNoPill v={a.waste_collection_access} />
                          </td>
                          <td className="text-center">
                            <YesNoPill v={a.sale_entry_access} />
                          </td>
                          <td className="text-center">
                            <YesNoPill v={a.income_access} />
                          </td>
                          <td className="text-center">
                            <YesNoPill v={a.expense_access} />
                          </td>

                          <td className="mm-actions-td" style={{ textAlign: "right" }}>
                            <div className="d-inline-flex gap-2">
                              <Button
                                variant="light"
                                className="mm-icon-btn primary"
                                disabled={!canUpdate || saving}
                                title={!canUpdate ? "No update permission" : "Edit"}
                                onClick={() => openEdit(a)}
                              >
                                <Edit2 size={16} />
                              </Button>

                              <Button
                                variant="light"
                                className="mm-icon-btn danger"
                                disabled={!canDelete || saving}
                                title={!canDelete ? "No delete permission" : "Delete"}
                                onClick={() => onDelete(a)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 ? (
                <div className="d-flex justify-content-center py-3 border-top mm-footer">
                  <Pagination className="mb-0 master-pagination">{getPaginationItems()}</Pagination>
                </div>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} centered size="lg" className="mm-modal">
        <Form onSubmit={onSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              {mode === "create" ? "Add Agent" : "Edit Agent"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Mobile</Form.Label>
                  <Form.Control
                    className="mm-input"
                    value={form.mobile}
                    onChange={(e) => {
                      const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setForm((p) => ({ ...p, mobile: onlyDigits }));
                    }}
                    placeholder="Enter mobile"
                    autoFocus
                    maxLength={10}
                  />
                  {form.mobile && !/^\d{10}$/.test(form.mobile) && (
                    <div className="text-danger small mt-1">
                      Mobile number must be exactly 10 digits
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Agent Name</Form.Label>
                  <Form.Control
                    className="mm-input"
                    value={form.ag_name}
                    onChange={(e) => setForm((p) => ({ ...p, ag_name: e.target.value }))}
                    placeholder="Enter name"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Password {mode === "edit" ? "(optional)" : ""}
                  </Form.Label>
                  <Form.Control
                    className="mm-input"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    placeholder={mode === "edit" ? "Leave blank to keep same" : "Enter password"}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold">Status</Form.Label>
                  <Form.Select
                    className="mm-select"
                    value={form.status}
                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12}>
                <div className="fw-bold mt-2 mb-2">Access</div>
                <div className="mm-access-grid">
                  <AccessSwitch k="water_access" label="Water Access" />
                  <AccessSwitch k="slrm_access" label="SLRM Access" />
                  <AccessSwitch k="profile_access" label="Profile Access" />
                  <AccessSwitch k="setting_access" label="Settings Access" />
                  <AccessSwitch k="balance_access" label="Balance Access" />
                  <AccessSwitch k="waste_collection_access" label="Waste Collection Access" />
                  <AccessSwitch k="sale_entry_access" label="Sale Entry Access" />
                  <AccessSwitch k="income_access" label="Income Access" />
                  <AccessSwitch k="expense_access" label="Expense Access" />
                </div>
              </Col>
            </Row>

            {(mode === "create" && !canCreate) || (mode === "edit" && !canUpdate) ? (
              <Alert variant="warning" className="mt-3">
                You don't have permission to {mode === "create" ? "create" : "update"} agents.
              </Alert>
            ) : null}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={saving}
              className="mm-cancel-btn"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              className="mm-primary-btn"
              disabled={
                saving ||
                !/^\d{10}$/.test(String(form.mobile || "").trim()) ||
                !String(form.ag_name || "").trim() ||
                (mode === "create" && !String(form.password || "").trim()) ||
                (mode === "create" && !canCreate) ||
                (mode === "edit" && !canUpdate)
              }
            >
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Saving...
                </>
              ) : mode === "create" ? (
                "Save Agent"
              ) : (
                "Update Agent"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ConfirmModal
        show={showDeleteModal}
        title="Delete Agent"
        message={`Delete agent "${deleteTarget?.ag_name || ""}"?`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />
    </div>
  );
}